import {
	ContextActionBar,
	getPropertySortText,
	OptionsMenu,
	OptionsMenuItem,
	PageContentTitle,
	SearchInputForm,
	SearchPlaceholder
} from "client/Components";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { DocumentsViewModel, DocumentsViewType } from "client/Api/Documents/SharePoint/common";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { DocumentPageLayout } from "../../DocumentPageLayout";
import {
	GridItem,
	RecycleBinActionContext,
	SharePointRecycleBin
} from "./SharePointRecycleBin";
import { FolderNavigationStackItem } from "../useFolderNavigationStack";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { localize } from "client_legacy/ndaw/culture";
import { Action, ActionsWithContext } from "client_legacy/ndaw/actions";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { newGuid } from "client_legacy/ndaw/guid";

export type SharePointRecycleBinPageContentProps = {
	isMobile: boolean;
	view: DocumentsViewModel;
	folderPath: readonly FolderNavigationStackItem[];
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
};

export const SharePointRecycleBinPageContent = (props: SharePointRecycleBinPageContentProps): ReactElement => {
	const {
		isMobile,
		view,
		folderPath,
		layoutKind,
		onLayoutKindChange
	} = props;

	if (view.type !== DocumentsViewType.RecycleBinView) {
		throw new Error("Must be a recycle bin view");
	}

	const [sortingState, setSortingState] = useState<DataSourceSortableState<GridItem>>({
		direction: DataSourceSortDirection.Descending,
		propertyName: "deletedOn"
	});

	const [actionsWithContext, setActionsWithContext] = useState<
		ActionsWithContext<RecycleBinActionContext, Action<RecycleBinActionContext>> | null
	>(null);

	const currentFolder = useMemo(
		(): FolderNavigationStackItem | undefined => {
			return folderPath[folderPath.length - 1];
		},
		[folderPath]
	);

	const isGridView: boolean = layoutKind === DocumentLayoutKind.Grid;

	const actionsChangeHandler = useCallback(
		(value: ActionsWithContext<RecycleBinActionContext, Action<RecycleBinActionContext>>): (() => void) => {
			setActionsWithContext(value);
			return () => setActionsWithContext(null);
		},
		[]
	);

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			return isNotNil(actionsWithContext)
				&& actionsWithContext.context
					.selection
					.length > 0;
		},
		[actionsWithContext, isMobile]
	);

	return (
		<DocumentPageLayout trackingId="sharepoint-page-content">
			<DocumentPageLayout.Header>
				{isMobile && (
					<DocumentPageLayout.BreadCrumb>
						<h3>{localize("_RecycleBin")}</h3>
					</DocumentPageLayout.BreadCrumb>
				)}
				{shouldShowActionBar
					? (
						<DocumentPageLayout.ActionBarContainer>
							<ContextActionBar
								{...(
									actionsWithContext
										? actionsWithContext
										: { actions: EMPTY_ARRAY, context: undefined! /* There are no actions, so no need for a context */ }
								)}
								trackingId="global-action-bar"
								horizontalAlignment={isMobile ? "default" : "page-content"}
								hasBorderBottom={isMobile}
							/>
						</DocumentPageLayout.ActionBarContainer>
					)
					: (
						<PageContentTitle
							{...(
								actionsWithContext
									? { actionContext: actionsWithContext.context, actions: actionsWithContext.actions}
									: { actions: undefined, actionContext: undefined }
							)}
							title={folderPath[folderPath.length - 1].name}
						/>
					)
				}
				<DocumentPageLayout.SortAndFilter>
					<SearchInputForm
						value={{
							value: "",
							version: STABLE_SEARCH_GUID
						}}
						onChange={() => {/**NOOP */}}
						placeholder={
							<SearchPlaceholder
								kind="search"
								properties={[localize("_Name")]}
							/>
						}
						height="large"
						isDisabled
					/>
					{(isMobile || isGridView) && (
						<OptionsMenu
							value={sortingState}
							icon="bars-sort"
							onChange={setSortingState}
							options={sortMenuOptions}
							trackingId="sort-menu"
							title={localize("_SortBy")}
							buttonSize="large"
						/>
					)}
					<DocumentPageLayout.LayoutKindChangeButton
						layoutKind={layoutKind}
						onLayoutKindChange={onLayoutKindChange}
					/>
				</DocumentPageLayout.SortAndFilter>
			</DocumentPageLayout.Header>

			<DocumentPageLayout.Content>
				{currentFolder && (
					<SharePointRecycleBin
						view={view}
						sortingState={sortingState}
						setSortingState={setSortingState}
						isMobile={isMobile}
						layoutKind={layoutKind}
						onActionsChanged={actionsChangeHandler}
					/>
				)}
			</DocumentPageLayout.Content>
		</DocumentPageLayout>
	);
};
SharePointRecycleBinPageContent.displayName = "SharePointRecycleBinPageContent";

const sortMenuOptions: readonly OptionsMenuItem<DataSourceSortableState<GridItem>>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateDeleted", "_NewToOld"),
		value: { propertyName: "deletedOn", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "created-on-new-old"
	},
	{
		text: getPropertySortText("_DateDeleted", "_OldToNew"),
		value: { propertyName: "deletedOn", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "created-on-old-new"
	},
	{
		text: getPropertySortText("_FileSize", "_SmallToLarge"),
		value: { propertyName: "size", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "file-size-small-large"
	},
	{
		text: getPropertySortText("_FileSize", "_LargeToSmall"),
		value: { propertyName: "size", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "file-size-large-small"
	}
];

const STABLE_SEARCH_GUID = newGuid();